import { useEffect, useMemo, useState } from 'react';
import { useAppKit } from '@reown/appkit/react';
import { useAppKitWallet, type Wallet as AppKitWallet } from '@reown/appkit-wallet-button/react';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { ArrowUpRight, ChevronRight, FlaskConical, Puzzle, QrCode, ShieldCheck, Smartphone, Wallet } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCross } from '@/hooks/use-cross';
import { isPreview } from '@/lib/config';

export function WalletDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (value: boolean) => void }) {
  const { connectPreview } = useCross();
  const { isConnected } = useAccount();
  const { open: openAppKit } = useAppKit();
  const { connect: connectAppKitWallet, error: appKitError, isPending: appKitPending } = useAppKitWallet({ namespace: 'eip155' });
  const { connectAsync, connectors, error: connectorError, isPending: connectorPending } = useConnect();
  const { disconnect } = useDisconnect();
  const [selectedWallet, setSelectedWallet] = useState<string>();
  const preview = isPreview();
  const isPending = appKitPending || connectorPending;

  const browserConnector = useMemo(() => connectors.find(connector => {
    const identity = `${connector.id} ${connector.name}`.toLowerCase();
    return !identity.includes('walletconnect') && !identity.includes('coinbase') && !identity.includes('safe');
  }), [connectors]);

  useEffect(() => {
    if (!isConnected) return;
    if (preview) connectPreview();
    onOpenChange(false);
  }, [connectPreview, isConnected, onOpenChange, preview]);

  const connectNamedWallet = async (wallet: AppKitWallet, label: string) => {
    setSelectedWallet(label);
    disconnect();
    try {
      await connectAppKitWallet(wallet);
    } catch {
      // The official AppKit connector error is shown below.
    }
  };

  const connectBrowserWallet = async () => {
    if (!browserConnector) {
      await connectNamedWallet('metamask', 'MetaMask');
      return;
    }
    setSelectedWallet(friendlyConnectorName(browserConnector.name));
    disconnect();
    try {
      await connectAsync({ connector: browserConnector });
    } catch {
      // Wagmi exposes the actionable connector error below.
    }
  };

  const openAllWallets = async () => {
    onOpenChange(false);
    await openAppKit({ view: 'Connect' });
  };

  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent>
    <DialogTitle>Connect your wallet</DialogTitle>
    <DialogDescription>{preview ? 'Choose how to connect. No signature, approval, transaction, or balance read is requested in this preview.' : 'CROSS reads supported assets on Robinhood Chain without taking custody of your portfolio.'}</DialogDescription>
    <div className="mt-6 space-y-3">
      <button type="button" className="group flex min-h-[76px] w-full items-center gap-4 rounded-[10px] border border-mint/35 bg-mint/[.055] px-4 text-left transition-colors hover:border-mint/65 hover:bg-mint/[.085] disabled:cursor-wait disabled:opacity-60" disabled={isPending} onClick={() => void connectNamedWallet('robinhood', 'Robinhood Wallet')}>
        <span className="grid size-11 shrink-0 place-items-center rounded-[9px] bg-mint text-[#02130d]"><Smartphone size={19} /></span>
        <span className="min-w-0 flex-1"><span className="flex items-center gap-2 text-[13px] font-semibold">Robinhood Wallet <span className="rounded-full border border-mint/30 px-2 py-0.5 text-[8px] uppercase tracking-[.1em] text-mint">Recommended</span></span><span className="mt-1 block text-[10px] leading-4 text-muted">Official mobile wallet · QR on desktop</span></span>
        <ChevronRight size={16} className="shrink-0 text-mint transition-transform group-hover:translate-x-0.5" />
      </button>
      <div className="grid grid-cols-2 gap-3 max-[460px]:grid-cols-1">
        <Button variant="outline" className="h-[62px] justify-between px-4" disabled={isPending} onClick={() => void connectNamedWallet('walletConnect', 'WalletConnect')}><span className="flex items-center gap-3"><QrCode size={17} className="text-mint" /><span className="text-left"><span className="block text-[11px] font-semibold">WalletConnect</span><span className="mt-1 block text-[9px] font-normal text-muted">Scan with mobile</span></span></span><ChevronRight size={13} /></Button>
        <Button variant="outline" className="h-[62px] justify-between px-4" disabled={isPending} onClick={() => void connectBrowserWallet()}><span className="flex items-center gap-3"><Puzzle size={17} className="text-mint" /><span className="text-left"><span className="block text-[11px] font-semibold">{browserConnector ? friendlyConnectorName(browserConnector.name) : 'MetaMask'}</span><span className="mt-1 block text-[9px] font-normal text-muted">{browserConnector ? 'Detected in browser' : 'Browser extension'}</span></span></span><ChevronRight size={13} /></Button>
      </div>
      <Button variant="ghost" className="h-11 w-full justify-between border border-transparent px-3 text-muted hover:border-line hover:text-white" disabled={isPending} onClick={() => void openAllWallets()}><span className="flex items-center gap-3"><Wallet size={16} />More wallets</span><ChevronRight size={14} /></Button>
      {preview && <><div className="flex items-center gap-3 py-1 text-[9px] uppercase tracking-[.12em] text-muted2"><span className="h-px flex-1 bg-line" />or continue without one<span className="h-px flex-1 bg-line" /></div><Button className="h-14 w-full justify-between" onClick={() => { connectPreview(); onOpenChange(false); }}><span className="flex items-center gap-3"><FlaskConical size={18} />Open demo workspace</span><ArrowUpRight size={16} /></Button></>}
    </div>
    {isPending && <div role="status" className="mt-4 flex items-center gap-3 text-[10px] text-muted"><span className="size-3 animate-spin rounded-full border border-line border-t-mint" />Opening {selectedWallet || 'wallet'}…</div>}
    {(appKitError || connectorError) && <div role="alert" className="mt-4 rounded-[9px] border border-danger/35 bg-danger/[.05] px-4 py-3 text-[10px] leading-5 text-red-200">{(appKitError || connectorError)?.message}</div>}
    <div className="mt-5 flex gap-3 rounded-[10px] border border-line bg-mint/[.025] p-4 text-[10px] leading-5 text-muted"><ShieldCheck size={15} className="mt-0.5 shrink-0 text-mint" /><span>{preview ? 'The wallet address is connected, but all displayed balances and orders remain clearly marked simulation data until the production deployment is activated.' : 'Connecting does not create a signature. Quote and transaction details are shown again before wallet authorization.'}</span></div>
  </DialogContent></Dialog>;
}

function friendlyConnectorName(name: string) {
  if (/injected/i.test(name)) {
    const provider = (window as Window & { ethereum?: { isRabby?: boolean; isMetaMask?: boolean } }).ethereum;
    if (provider?.isRabby) return 'Rabby';
    if (provider?.isMetaMask) return 'MetaMask';
    return 'Browser wallet';
  }
  return name.replace(/injected/gi, 'Browser wallet');
}
